import type { FC, ReactNode } from 'react'
import { Box, Card } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import { mergeSx } from '~/shared/lib/mergeSx'

interface SectionCardProps {
  children: ReactNode
  /**
   * Left accent bar color. Pass a theme-derived color (usually a tone
   * foreground, e.g. `theme.palette.tones.error.fg`).
   */
  accentColor?: string
  /** Footer strip rendered on `background.cardFooter` below a divider. */
  footer?: ReactNode
  /** Body padding defaults to theme spacing 2; disable when rows own it. */
  disablePadding?: boolean
  /**
   * Styles for the internal body wrapper. Needed when the card participates
   * in a sizing chain (e.g. a full-bleed iframe): the body is a plain block
   * otherwise, so percentage/flex sizing of children stops at it.
   */
  bodySx?: SxProps<Theme>
  sx?: SxProps<Theme>
  'data-testid'?: string
}

/**
 * Content card for detail surfaces: divider border, theme-shape radius, the
 * resting card shadow (`theme.shadows[1]`), optional left accent bar and
 * footer strip. All values resolve from the active theme.
 */
export const SectionCard: FC<SectionCardProps> = ({
  children,
  accentColor,
  footer,
  disablePadding,
  bodySx = [],
  sx = [],
  ...rest
}) => (
  <Card
    variant="outlined"
    sx={mergeSx(
      theme => ({
        boxShadow: theme.shadows[1],
        overflow: 'hidden',
        ...(accentColor ? { borderLeft: `3px solid ${accentColor}` } : {}),
      }),
      sx,
    )}
    {...rest}
  >
    <Box sx={mergeSx({ p: disablePadding ? 0 : 2 }, bodySx)}>{children}</Box>
    {footer && (
      <Box
        sx={theme => ({
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: 'background.cardFooter',
          px: 2,
          py: 1.25,
        })}
      >
        {footer}
      </Box>
    )}
  </Card>
)
